import React, { useEffect, useState } from "react";
import ToastMessage from "../../../../common/ToastMessage";
import CustomDataGrid from '../../../../common/CustomDataGrid';
import useAssetDepartments from "../../../../store/ReactQueries/assets/assetDepartments/useAssetDepartments";
import useAddAssetDepartment from "../../../../store/ReactQueries/assets/assetDepartments/useAddAssetDepartment";
import useUpdateAssetDepartment from "../../../../store/ReactQueries/assets/assetDepartments/useUpdateAssetDepartment";
import useDeleteAssetDepartment from "../../../../store/ReactQueries/assets/assetDepartments/useDeleteAssetDepartment";
import { GridColDef } from "@mui/x-data-grid";
import { PAGESIZE } from "../../../../utils/constants";
import { FieldErrors } from "../../../../domain/helpers/Errors";
import { formatDate } from "../../../../common/GlobalFunctions";
import { validateSchema } from "../../../../common/ValidateSchema";
import { useLanguageStore } from "../../../../store/useLanguageStore";
import { AssetDepartmentInterface } from "../../../../models/AssetModel/AssetDepartment";
import { assetDepartmentsSchema } from "../../../../domain/ValidationSchemas/Asset/assetDepartment";

export const AssetDepartments = () => {

    const { language } = useLanguageStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<AssetDepartmentInterface>({
        id: "",
        title: "",
    });

    const [modal, setModal] = useState<{
        add: boolean;
        edit: boolean;
        view: boolean;
    }>({
        add: false,
        edit: false,
        view: false,
    });

    let { data: assetDepartments, error } = useAssetDepartments(page, pageSize);

    const addAssetCategory = useAddAssetDepartment(page, pageSize);
    const updateAssetCategory = useUpdateAssetDepartment(page, pageSize);
    const deleteAssetCategory = useDeleteAssetDepartment(page, pageSize);

    useEffect(() => {
        if (error) ToastMessage.Failed(`Error: ${error.message}`);
    }, [error]);

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: "Id",
            type: "string",
            editable: false,
            width: 200,
        },
        {
            field: "title",
            headerName: "Title",
            type: "string",
            editable: true,
            width: 250,
        },
        {
            field: "createdDate",
            headerName: "CreatedDate",
            type: "dateTime",
            editable: false,
            width: 150,
            valueFormatter: formatDate,
        },
        {
            field: "updatedDate",
            headerName: "UpdatedDate",
            type: "dateTime",
            editable: false,
            width: 150,
            valueFormatter: formatDate,
        },
    ];

    const addItems = () => {
        setForm({
            ...form,
            title: "",
            id: "",
        });
        setModal({ ...modal, add: true });
        setErrors({});
    };

    const handleError = (error: unknown) => {
        if (error instanceof Error) {
            console.log(error.message);
            ToastMessage.Failed(error.message);
        } else {
            console.log("An unknown error occurred");
            ToastMessage.Failed();
        }
    };

    const handleSave = async () => {
        const isValid = validateSchema(assetDepartmentsSchema, { title: form.title });

        setErrors(isValid.errors);
        if (!isValid.result) return;

        const upForm: AssetDepartmentInterface = {
            id: form.id,
            title: form.title,
        };

        if (upForm.id === "") {
            const { id, ...formData } = upForm;
            addAssetCategory.mutate(formData, {
                onSuccess: () => {
                    ToastMessage.Success();
                },
                onError: (error) => handleError(error),
            });
        } else {
            updateAssetCategory.mutate(upForm, {
                onSuccess: () => {
                    ToastMessage.Success();
                },
                onError: (error) => handleError(error),
            });
        }

        setModal({ ...modal, add: false, edit: false, view: false });
    };

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language["ToastDeleteItem"])) {
            deleteAssetCategory.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    return (
        <CustomDataGrid
            name={language["AssetDepartments"]}
            rows={assetDepartments || []}
            form={form}
            page={page}
            modal={modal}
            errors={errors}
            columns={columns}
            pageSize={pageSize}
            setForm={setForm}
            setPage={setPage}
            setModal={setModal}
            addItems={addItems}
            setErrors={setErrors}
            handleSave={handleSave}
            setPageSize={setPageSize}
            handleDelete={handleDelete}
            showColumnModal={["title"]}
            hideColumns={["authorId", "id", "tenantId"]}
        />
    );
};
export default AssetDepartments;
